/**
 * The Venom Altar theme (docs/art/venom-altar.md): an overgrown serpent-cult temple in the jungle,
 * RuneScape-style. Since v2 (docs/design/dungeon-layout-study.md) it is an enclosed monastery cut
 * into the cliff: full-height walls, vaults / timber roofs / an arcade, under a camera cutaway. Warm sandstone walls and ledges, calm grey-olive floors that change pattern per
 * area, jade + gold cult accents, dense edge clutter on raised ledges, jungle overgrowth.
 * Pure data; asset ids come from ./manifest.ts, 'proc:*' ids from ./procs.ts.
 */
import type { PropHint } from '@keystone/sim';
import type { DungeonTheme, KitItem, PropKit, RGB, RoomStyle, WallItem } from '../types';

/** Palette tokens (sRGB hex), art bible §2. */
export const VENOM_PALETTE = {
  floorStone: 0x5b5d56,
  floorDirt: 0x4d4537,
  stoneSand: 0x9a8460,
  stoneSandLight: 0xbca67a,
  stoneSandShadow: 0x5e5040,
  stoneMossWall: 0x5f6d3e,
  stoneCliff: 0x4a4236,
  jade: 0x3f8f78,
  jadeDeep: 0x24594c,
  jadeLight: 0x7cc4a6,
  gold: 0xc99a45,
  bone: 0xd8ccb0,
  blood: 0x7d2433,
  wood: 0x6b4a2e,
  waterPoison: 0x1f3d2c,
  lightBrazier: 0xff9a3c,
  lightJade: 0x4fbf9a,
} as const;

const warmVar: readonly [RGB, RGB] = [
  [0.86, 0.84, 0.8],
  [1.05, 1.0, 0.92],
];
const stoneVar: readonly [RGB, RGB] = [
  [0.8, 0.78, 0.72],
  [1.0, 0.97, 0.9],
];
const leafVar: readonly [RGB, RGB] = [
  [0.75, 0.9, 0.7],
  [1.0, 1.08, 0.86],
];
const glassVar: readonly [RGB, RGB] = [
  [0.55, 0.68, 0.6],
  [0.7, 0.82, 0.72],
];
const boneVar: readonly [RGB, RGB] = [
  [0.85, 0.82, 0.74],
  [1.0, 0.97, 0.9],
];

const k = (asset: string, w: number, scale: readonly [number, number], r: number, h: number, extra: Partial<KitItem> = {}): KitItem => ({ asset, w, scale, r, h, ...extra });

// Shared kit fragments --------------------------------------------------------------
const FERNS: KitItem[] = [
  k('kn_plant_bush', 3, [3.4, 4.4], 0.2, 0.24, { tint: leafVar, detail: true }),
  k('kn_plant_bushDetailed', 2, [3.2, 4.0], 0.3, 0.36, { tint: leafVar, detail: true }),
  k('kn_plant_bushLarge', 2, [3.6, 4.6], 0.19, 0.24, { tint: leafVar, detail: true }),
  k('kn_plant_flatTall', 2, [3.6, 4.6], 0.14, 0.28, { tint: leafVar, detail: true }),
  k('kn_grass_large', 2, [3.2, 4.2], 0.2, 0.25, { tint: leafVar, detail: true }),
  k('kn_mushroom_tanGroup', 1, [3.0, 3.6], 0.14, 0.25, { detail: true }),
];
const BONES: KitItem[] = [
  k('kh_bone_A', 2, [1.2, 1.5], 0.4, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_bone_C', 2, [1.2, 1.5], 0.55, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_skull', 1, [1.1, 1.4], 0.46, 0.9, { tint: boneVar }),
];
const CANDLES: KitItem[] = [
  k('kd_candle_triple', 2, [1.3, 1.6], 0.25, 0.87, { detail: true }),
  k('kd_candle_melted', 2, [1.3, 1.6], 0.17, 0.7, { detail: true }),
  k('kd_candle_lit', 1, [1.3, 1.6], 0.17, 1.05, { detail: true }),
];
const RUBBLE: KitItem[] = [
  k('kn_rock_largeA', 2, [3.2, 4.2], 0.45, 0.26, { tint: stoneVar }),
  k('kn_rock_largeC', 1, [3.2, 4.0], 0.52, 0.32, { tint: stoneVar }),
  k('kn_rock_smallA', 2, [3.2, 4.0], 0.18, 0.19, { tint: stoneVar, detail: true }),
  k('kn_rock_smallFlatA', 1, [3.2, 4.2], 0.25, 0.07, { tint: stoneVar, detail: true }),
  k('kn_statue_columnDamaged', 2, [3.6, 4.6], 0.18, 0.7, { tint: stoneVar }),
  k('kn_statue_block', 1, [3.2, 4.0], 0.24, 0.4, { tint: stoneVar }),
];

const wi = (asset: string, w: number, scale: readonly [number, number], y: number, width: number, extra: Partial<WallItem> = {}): WallItem => ({ asset, w, scale, y, width, ...extra });
const BANNERS_GREEN: WallItem[] = [
  wi('kd_banner_thin_green', 3, [1, 1.1], 0, 1.1, { off: 0.5, farOnly: true }),
  wi('kd_banner_patternA_green', 2, [1, 1.1], 0, 1.5, { off: 0.5, farOnly: true }),
  wi('kd_banner_triple_green', 1, [1, 1], 0, 3.7, { off: 0.5, farOnly: true }),
];
const BANNERS_RED: WallItem[] = [
  wi('kd_banner_triple_red', 2, [1, 1], 0, 3.7, { off: 0.5, farOnly: true }),
  wi('kd_banner_patternC_red', 2, [1, 1.1], 0, 1.5, { off: 0.5, farOnly: true }),
];

/** Low floor debris (flat rocks, bones, leaves): ≤ 0.6 yd tall so it never hides a telegraph. */
const DEBRIS: KitItem[] = [
  k('kn_rock_smallFlatA', 3, [3.0, 4.2], 0.25, 0.07, { tint: stoneVar, detail: true }),
  k('kn_rock_smallA', 2, [2.2, 2.8], 0.15, 0.19, { tint: stoneVar, detail: true }),
  k('kh_bone_A', 2, [1.1, 1.4], 0.4, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  k('kh_bone_C', 1, [1.1, 1.4], 0.55, 0.28, { tint: boneVar, detail: true, lift: 0.12 }),
  // Procedural fern tufts and sandstone rubble instead of Kenney's flat leaf card, which read as a
  // flat green hexagon from the default camera.
  k('proc:fern', 3, [0.9, 1.25], 0.6, 0.6, { tint: leafVar, detail: true }),
  k('proc:rubble', 2, [0.9, 1.2], 0.75, 0.6, { tint: stoneVar, detail: true }),
];

/** Archetype debris on top of the shared set (all ≤ 0.75 yd tall at max scale). */
const debrisWith = (...extra: KitItem[]): KitItem[] => [...DEBRIS, ...extra];
const STRAW_LOW = k('proc:straw', 3, [0.9, 1.3], 0.8, 0.3, { detail: true });
const SHELLS_LOW = k('proc:eggshell', 2, [0.8, 1.2], 0.45, 0.35, { detail: true });
const POT_BROKEN = k('proc:urn_tipped', 1, [0.8, 0.95], 0.5, 0.75, { tint: warmVar, detail: true });
/** Small offering urn (procedural; Kenney's small pot read as a purple hexagon from above). */
const URN = (w: number, extra: Partial<KitItem> = {}): KitItem => k('proc:urn', w, [1.0, 1.25], 0.42, 0.97, { tint: warmVar, ...extra });
const MUSHROOMS_LOW = k('kn_mushroom_tanGroup', 1, [2.4, 2.9], 0.14, 0.25, { detail: true });
const COINS_LOW = k('kd_coin_stack_small', 2, [1.0, 1.3], 0.5, 0.48, { detail: true });
const PLATE_LOW = k('kd_plate', 1, [1.1, 1.4], 0.5, 0.13, { detail: true });

const kit = (p: Partial<PropKit> & Pick<PropKit, 'clutter'>): PropKit => ({
  debris: DEBRIS,
  islands: 1.8,
  cluster: [2, 4],
  spacing: 4.6,
  wall: BANNERS_GREEN,
  wallSpacing: 7,
  foliage: FERNS,
  foliageChance: 0.45,
  vines: 0.35,
  torchSpacing: 12, // +dungeon2: enclosed halls need tighter warm pools (study: lights every ~10–14 yd)
  decals: 0.55,
  ...p,
});

export const VENOM_KITS: Record<string, PropKit> = {
  entrance: kit({
    debris: debrisWith(POT_BROKEN, MUSHROOMS_LOW),
    clutter: [
      ...RUBBLE,
      k('kn_pot_large', 1, [3.2, 3.8], 0.28, 0.2, { tint: warmVar }),
      URN(2, { detail: true }),
      k('kd_box_small_decorated', 1, [1.0, 1.3], 0.75, 1.48, { tint: warmVar }),
      k('kn_log', 1, [3.0, 3.6], 0.36, 0.17, { tint: warmVar }),
      k('kh_tree_dead_small', 1, [1.2, 1.5], 0.6, 2.7, {}),
    ],
    foliageChance: 0.7,
    vines: 0.6,
    spacing: 4.2,
  }),
  pens: kit({
    debris: debrisWith(STRAW_LOW, STRAW_LOW, SHELLS_LOW, PLATE_LOW),
    clutter: [
      k('kd_barrel_small', 3, [1.2, 1.45], 0.5, 1.02, { tint: warmVar }),
      k('kd_barrel_small_stack', 2, [1.15, 1.35], 0.95, 1.77, { tint: warmVar }),
      k('kd_box_small_decorated', 2, [1.1, 1.35], 0.75, 1.48, { tint: [[0.78, 0.74, 0.55], [0.95, 0.9, 0.62]] }),
      k('kd_plate', 1, [1.2, 1.4], 0.5, 0.13, { detail: true }),
      k('kd_table_long_broken', 1, [1.1, 1.3], 1.3, 1.27, { tint: warmVar }),
      ...BONES,
      k('proc:eggshell', 2, [0.8, 1.2], 0.45, 0.35, { detail: true }),
      k('proc:straw', 3, [0.9, 1.3], 0.8, 0.3, { detail: true }),
    ],
    wall: [...BANNERS_GREEN, wi('kd_keyring_hanging', 2, [1.3, 1.5], 2.3, 0.7, { off: -0.25 })],
  }),
  arenaPit: kit({
    debris: debrisWith(STRAW_LOW),
    clutter: [
      ...BONES,
      ...BONES,
      k('kn_rock_largeC', 2, [3.4, 4.4], 0.52, 0.32, { tint: stoneVar }),
      k('kn_rock_smallA', 2, [3.2, 4.0], 0.18, 0.19, { tint: stoneVar, detail: true }),
      k('proc:stake', 3, [0.9, 1.3], 0.3, 1.8, {}),
      k('proc:straw', 2, [1.0, 1.4], 0.8, 0.3, { detail: true, tint: [[0.55, 0.45, 0.38], [0.7, 0.55, 0.42]] }),
      k('kh_tree_dead_small', 1, [1.2, 1.5], 0.6, 2.7, {}),
    ],
    cluster: [2, 3],
    spacing: 5.2,
    foliageChance: 0.2,
    wall: [wi('proc:chain', 3, [1, 1.2], 1.0, 0.6, { off: -0.15, farOnly: true })],
    torchSpacing: 16,
  }),
  barracks: kit({
    debris: debrisWith(PLATE_LOW, POT_BROKEN),
    clutter: [
      k('kd_barrel_small_stack', 2, [1.15, 1.35], 0.95, 1.77, { tint: warmVar }),
      k('kd_barrel_small', 2, [1.2, 1.45], 0.5, 1.02, { tint: warmVar }),
      k('kd_box_small_decorated', 2, [1.1, 1.35], 0.75, 1.48, { tint: warmVar }),
      k('kd_bed_floor', 2, [1.1, 1.3], 1.4, 0.56, { tint: warmVar }),
      k('kd_stool', 2, [1.3, 1.5], 0.4, 0.5, { detail: true }),
      k('kd_plate_food_A', 1, [1.2, 1.4], 0.5, 0.79, { detail: true }),
      k('kd_trunk_large_A', 1, [1.2, 1.4], 0.8, 1.0, { tint: warmVar }),
      k('kd_table_medium_decorated_A', 1, [1.1, 1.3], 1.1, 1.89, { tint: warmVar }),
    ],
    wall: [...BANNERS_GREEN, wi('kd_sword_shield', 3, [1.25, 1.4], 1.6, 2.2, { off: -0.1 }), wi('kd_sword_shield_broken', 1, [1.25, 1.4], 1.6, 1.5, { off: -0.1 })],
    foliageChance: 0.15,
    vines: 0.15,
    spacing: 4.2,
  }),
  hatchery: kit({
    debris: debrisWith(STRAW_LOW, STRAW_LOW, SHELLS_LOW, SHELLS_LOW, MUSHROOMS_LOW),
    islands: 2.2,
    clutter: [
      k('proc:straw', 3, [1.0, 1.4], 0.8, 0.3, { detail: true }),
      k('proc:egg', 3, [0.8, 1.3], 0.35, 0.8, { tint: boneVar }),
      k('proc:eggshell', 2, [0.8, 1.2], 0.45, 0.35, { detail: true }),
      ...BONES,
      k('kd_barrel_small', 1, [1.2, 1.45], 0.5, 1.02, { tint: warmVar }),
      k('kn_mushroom_tanGroup', 2, [3.0, 3.8], 0.14, 0.25, { detail: true }),
    ],
    foliageChance: 0.7,
    vines: 0.7,
    spacing: 3.8,
    cluster: [3, 5],
  }),
  arenaSanctum: kit({
    clutter: [
      URN(2),
      k('kn_pot_large', 1, [3.2, 3.8], 0.28, 0.2, { tint: warmVar }),
      ...CANDLES,
      k('kn_rock_largeA', 1, [3.2, 4.2], 0.45, 0.26, { tint: stoneVar }),
      k('kn_lily_large', 1, [3.2, 4.0], 0.2, 0.1, { tint: leafVar, detail: true }),
    ],
    cluster: [2, 3],
    spacing: 5.5,
    foliageChance: 0.55,
    vines: 0.75,
    torchSpacing: 0,
    wall: BANNERS_GREEN,
  }),
  pools: kit({
    debris: debrisWith(MUSHROOMS_LOW, POT_BROKEN),
    clutter: [
      k('kd_bottle_A_green', 2, [1.3, 1.6], 0.2, 0.89, { detail: true, tint: glassVar }),
      k('kd_bottle_B_green', 2, [1.3, 1.6], 0.28, 0.89, { detail: true, tint: glassVar }),
      k('kd_bottle_A_labeled_green', 2, [1.3, 1.6], 0.2, 0.89, { detail: true, tint: glassVar }),
      k('kd_barrel_large_decorated', 2, [1.0, 1.2], 1.0, 2.56, { tint: warmVar }),
      k('kd_keg', 1, [1.0, 1.2], 1.0, 2.05, { tint: warmVar }),
      k('kd_table_medium_decorated_A', 1, [1.1, 1.3], 1.1, 1.89, { tint: warmVar }),
      ...BONES,
    ],
    foliageChance: 0.5,
    vines: 0.6,
    torchSpacing: 22,
  }),
  treasure: kit({
    debris: debrisWith(COINS_LOW, COINS_LOW, POT_BROKEN),
    clutter: [
      k('kd_coin_stack_small', 3, [1.3, 1.6], 0.5, 0.48, { detail: true }),
      k('kd_coin_stack_medium', 2, [1.3, 1.6], 0.55, 0.64, {}),
      k('kd_chest', 1, [1.2, 1.4], 0.95, 1.3, {}),
      URN(2, { tint: [[1.0, 0.85, 0.55], [1.1, 0.95, 0.65]] }),
      ...CANDLES,
      k('kd_key', 1, [1.3, 1.5], 0.5, 0.4, { detail: true, lift: 0.07 }),
    ],
    wall: [wi('kd_banner_patternB_yellow', 1, [1, 1.1], 0, 1.5, { off: 0.5, farOnly: true }), ...BANNERS_GREEN, wi('kd_shelf_small_candles', 2, [1.3, 1.5], 1.5, 1.2, { off: 0 })],
    foliageChance: 0.25,
  }),
  ritual: kit({
    debris: debrisWith(POT_BROKEN),
    clutter: [
      k('kh_skull_candle', 2, [1.2, 1.4], 0.46, 1.19, {}),
      k('kh_shrine_candles', 1, [1.2, 1.4], 0.6, 1.79, {}),
      ...CANDLES,
      ...BONES,
      URN(1, { detail: true }),
    ],
    wall: [...BANNERS_RED, ...BANNERS_GREEN.slice(0, 1)],
    foliageChance: 0.3,
  }),
  arenaSummit: kit({
    clutter: [
      k('kh_post_skull', 2, [1.2, 1.4], 0.6, 3.3, {}),
      ...BONES,
      k('kd_coin_stack_small', 1, [1.3, 1.6], 0.5, 0.48, { detail: true }),
      k('kn_pot_large', 1, [3.2, 3.8], 0.28, 0.2, { tint: [[1.0, 0.85, 0.55], [1.1, 0.95, 0.65]] }),
      ...CANDLES,
    ],
    cluster: [2, 3],
    spacing: 5.4,
    wall: BANNERS_RED,
    foliageChance: 0.3,
  }),
  // +dungeon2: the scriptorium (SM-library echo): scroll cases, desks, candles, shelves on the walls.
  library: kit({
    debris: debrisWith(PLATE_LOW, POT_BROKEN),
    clutter: [
      k('proc:bookcase', 4, [0.95, 1.05], 1.6, 3.9, { yaw: 0.12 }),
      k('proc:lectern', 2, [1.0, 1.1], 0.6, 1.8, {}),
      k('kd_table_medium_decorated_A', 1, [1.1, 1.3], 1.1, 1.89, { tint: warmVar }),
      k('kd_stool', 2, [1.3, 1.5], 0.4, 0.5, { detail: true }),
      ...CANDLES,
      k('kd_trunk_large_A', 1, [1.2, 1.4], 0.8, 1.0, { tint: warmVar }),
      URN(1, { detail: true }),
    ],
    cluster: [1, 3],
    spacing: 3.6,
    wall: [wi('kd_shelves', 3, [1.3, 1.5], 0.4, 2.6, { off: 0 }), wi('kd_shelf_small_candles', 2, [1.3, 1.5], 1.5, 1.2, { off: 0 }), ...BANNERS_GREEN],
    wallSpacing: 5,
    foliageChance: 0.12,
    vines: 0.12,
    torchSpacing: 11,
  }),
  // +dungeon2: the mine wing (Deadmines echo): crates, kegs, ore, lanterns, eggs in straw.
  mine: kit({
    debris: debrisWith(STRAW_LOW, SHELLS_LOW, MUSHROOMS_LOW),
    clutter: [
      k('kd_barrel_small', 3, [1.2, 1.45], 0.5, 1.02, { tint: warmVar }),
      k('kd_barrel_small_stack', 2, [1.15, 1.35], 0.95, 1.77, { tint: warmVar }),
      k('kd_box_small_decorated', 3, [1.1, 1.35], 0.75, 1.48, { tint: [[0.78, 0.74, 0.62], [0.95, 0.9, 0.72]] }),
      k('kd_keg', 1, [1.0, 1.2], 1.0, 2.05, { tint: warmVar }),
      k('proc:ore_pile', 2, [0.55, 0.75], 1.0, 0.6, {}),
      k('kh_lantern_standing', 1, [1.4, 1.6], 0.4, 1.4, {}),
      k('proc:egg', 2, [0.8, 1.2], 0.35, 0.8, { tint: boneVar }),
      k('proc:straw', 2, [1.0, 1.4], 0.8, 0.3, { detail: true }),
      k('kn_log', 1, [3.0, 3.6], 0.36, 0.17, { tint: warmVar }),
    ],
    cluster: [2, 4],
    spacing: 3.8,
    wall: [wi('kd_keyring_hanging', 1, [1.3, 1.5], 2.3, 0.7, { off: -0.25 })],
    wallSpacing: 9,
    foliageChance: 0.3,
    vines: 0.3,
    torchSpacing: 10,
  }),
  // +dungeon2: the cloister arcade walk: offerings, potted plants, eggs, benches under the roof.
  cloister: kit({
    debris: debrisWith(MUSHROOMS_LOW, SHELLS_LOW),
    islands: 3,
    clutter: [
      k('kn_pot_large', 2, [3.2, 3.8], 0.28, 0.2, { tint: warmVar }),
      URN(3),
      ...CANDLES,
      k('kd_table_long_broken', 1, [1.1, 1.3], 1.3, 1.27, { tint: warmVar }),
      k('proc:egg', 1, [0.8, 1.2], 0.35, 0.8, { tint: boneVar }),
      k('kn_statue_head', 1, [2.6, 3.2], 0.6, 1.4, { tint: stoneVar }),
    ],
    foliageChance: 0.8,
    vines: 0.7,
    spacing: 4,
    torchSpacing: 12,
  }),
  corridor: kit({
    clutter: [...CANDLES, k('kh_skull_candle', 1, [1.0, 1.2], 0.46, 1.19, { detail: true }), URN(1, { detail: true, scale: [0.95, 1.1] })],
    cluster: [1, 2],
    spacing: 6,
    foliageChance: 0.4,
  }),
};

/**
 * v2 rooms (docs/design/dungeon-layout-study.md §5–6): wall heights and overheads follow the study
 * (8 yd halls, a 6.5 yd cloister with a lean-to arcade, 9–12 yd boss halls, a 6 yd timbered mine
 * cavern). Walls stand on the 0.6 yd dressing ledge, so the spring line is ledge + wallH.
 */
const ROOMS: Record<string, RoomStyle> = {
  gate: { archetype: 'entrance', floor: 'flagstone', light: 'neutral', path: 'tiles', border: 'tiles', wallH: 8, ceiling: 'vault' },
  cloister: { archetype: 'cloister', floor: 'flagstone', light: 'warm', border: 'tiles', wallH: 6.5, arcade: { inset: 10, low: 5.2, garth: 'dirt' } },
  ravager_pit: { archetype: 'arenaPit', floor: 'dirtDark', margin: 4, light: 'warm', border: 'flagstone', wallH: 9, ceiling: 'cave' },
  scriptorium: { archetype: 'library', floor: 'tiles', light: 'warm', path: 'runner', border: 'runner', wallH: 8, ceiling: 'vault' },
  hatchery: { archetype: 'mine', floor: 'dirt', light: 'warm', path: 'flagstone', wallH: 6, ceiling: 'beams' },
  coil_cistern: { archetype: 'arenaSanctum', floor: 'wet', margin: 4, light: 'jade', border: 'tiles', wallH: 9, ceiling: 'vault' },
  venom_crypt: { archetype: 'pools', floor: 'wet', light: 'jade', path: 'tiles', border: 'tiles', wallH: 7, ceiling: 'vault' },
  treasury: { archetype: 'treasure', floor: 'tiles', light: 'warm', path: 'runner', border: 'runner', wallH: 7, ceiling: 'vault' },
  fang_stair: { archetype: 'ritual', floor: 'tiles', light: 'warm', path: 'runner', border: 'runner', wallH: 9, ceiling: 'vault' },
  fang_altar: { archetype: 'arenaSummit', floor: 'tiles', margin: 4, light: 'warm', border: 'runner', wallH: 12, ceiling: 'vault' },
};

/** The mine wing's tunnels: low, timbered, dirt-floored (Deadmines echo). */
const MINE_TUNNELS = new Set(['c_scriptorium_hatchery', 'c_hatchery_cistern']);

export const venomAltarTheme: DungeonTheme = {
  id: 'venom_altar',
  name: 'The Venom Altar',
  // Floors: calm, mid-dark, grey-olive; the tints pull the generated textures slightly warm so the
  // green-leaning hemisphere doesn't push them toward lime.
  floors: {
    flagstone: { texture: 'venom_floor_flagstone', tint: 0xf4eee6, tileYd: 7 },
    dirt: { texture: 'venom_floor_dirt_roots', tint: 0xfff6ee, tileYd: 6 },
    dirtDark: { texture: 'venom_floor_dirt_roots', tint: 0xeee2da, tileYd: 6, base: 0.9 },
    tiles: { texture: 'venom_floor_temple_tiles', tint: 0xf4e9dc, tileYd: 10 },
    wet: { texture: 'venom_floor_flagstone', tint: 0xd8e0dc, tileYd: 7, base: 0.86 },
    /** Tiled halls: a flagstone runner between the doorways (a pattern change, same value band). */
    runner: { texture: 'venom_floor_flagstone', tint: 0xf6efe4, tileYd: 5, base: 1.04 },
  },
  surfaces: {
    ledgeTop: { texture: 'venom_floor_temple_tiles', tint: 0xd8c39a, tileYd: 6 },
    stone: { texture: 'venom_wall_sandstone', tint: 0xffffff, tileYd: 4 },
    cliff: { texture: 'venom_wall_sandstone', tint: 0x8a7c66, tileYd: 4 },
    trim: { texture: 'venom_trim_serpent', tint: 0xffffff, tileYd: 2.4 },
    wood: { texture: 'venom_wood_planks', tint: 0xffffff, tileYd: 3 },
    water: { texture: 'venom_water_poison', tint: 0xffffff, tileYd: 6 },
  },
  arch: {
    ledgeHeight: 0.6,
    roomMargin: 3,
    corridorMargin: 1.2,
    wallThickness: 2.4,
    wallHeights: { far: 4, near: 1.6, side: 2.8 },
    baseY: -2.5,
    trim: true,
    // +dungeon2: enclosed interiors with a camera cutaway (cutaway.ts).
    enclosed: { roomWallH: 8, corridorWallH: 5, cutHeight: 1.7, vaultRise: 0.32 },
  },
  room: (r) => ROOMS[r.id] ?? { archetype: 'entrance', floor: 'flagstone', light: 'neutral' },
  corridorFloor: (c) => (MINE_TUNNELS.has(c.id) ? 'dirt' : 'flagstone'),
  corridorStyle: (c) => (MINE_TUNNELS.has(c.id) ? { wallH: 4.5, ceiling: 'beams' } : c.id === 'c_cloister_pit' ? { wallH: 5, ceiling: 'cave' } : { wallH: 5, ceiling: 'vault' }),
  // One cluster per room (corridors join their `from` room): rooms are big, so per-room meshes
  // keep both the main and the shadow pass to the one or two rooms actually on screen.
  kits: VENOM_KITS,
  heroes: {
    ruined_gate: { half: [5.5, 1.2], h: 3 },
    cobra_statue: { half: [1.0, 1.0], h: 4.2, light: 'jade' },
    cage: { half: [2.4, 1.6], h: 2.4 },
    ribcage: { half: [2.2, 1.6], h: 2.6 },
    skull_mound: { half: [1.6, 1.4], h: 1.6 },
    weapon_rack: { half: [2.2, 0.7], h: 2.4 },
    war_table: { half: [1.4, 2.6], h: 1.9 },
    egg_clutch: { half: [1.5, 1.3], h: 1.0, light: 'warm' },
    coiled_serpent: { half: [6.5, 2.2], h: 6.5, light: 'jade' },
    floor_mosaic: { half: [1, 1], h: 0, floorDecal: true },
    poison_pool: { half: [3.6, 1.6], h: 0, light: 'jade' },
    idol: { half: [1.6, 1.6], h: 4.4, light: 'warm' },
    brazier: { half: [0.9, 0.9], h: 2, flame: true, light: 'warm' },
    gold_pile: { half: [1.5, 1.0], h: 1.3 },
    war_totem: { half: [0.8, 0.8], h: 3.6 },
    drum: { half: [1.1, 1.1], h: 1.4 },
    altar_dais: { half: [3.4, 1.8], h: 2.2, light: 'warm' },
    fang_jaw: { half: [4.2, 0.8], h: 5.5 },
    // +dungeon2
    serpent_fountain: { half: [3.6, 3.6], h: 5.2, light: 'jade' },
    bookcase: { half: [1.7, 0.55], h: 3.9 },
    lectern: { half: [0.7, 0.6], h: 1.8, light: 'warm' },
    mine_cart: { half: [2.6, 1.0], h: 2.0 },
    ore_pile: { half: [1.8, 1.0], h: 1.0 },
    sarcophagus: { half: [1.9, 1.0], h: 1.5, light: 'jade' },
    arch: { half: [1, 1], h: 4.6, opening: true },
    arch_gold: { half: [1, 1], h: 4.6, opening: true },
  },
  extraHints: (d) => {
    const out: PropHint[] = [];
    // Plaza medallions where the worn paths meet in the big halls (low-contrast floor decal).
    for (const [id, scale] of [['gate', 7], ['scriptorium', 7.5], ['fang_stair', 6.5], ['treasury', 6.5]] as const) {
      const r = d.rooms.find((x) => x.id === id);
      if (!r) continue;
      const xs = r.shape.points.map((p) => p.x);
      const ys = r.shape.points.map((p) => p.y);
      out.push({ kind: 'floor_mosaic', pos: { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 }, scale, roomId: id });
    }
    const altar = d.rooms.find((r) => r.id === 'fang_altar');
    if (altar) {
      const xs = altar.shape.points.map((p) => p.x);
      const ys = altar.shape.points.map((p) => p.y);
      out.push({ kind: 'floor_mosaic', pos: { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 + 2 }, scale: 12, roomId: 'fang_altar' });
    }
    return out;
  },
  // Light only falls in through the cliff gate and the open cloister garth (enclosed elsewhere).
  shafts: { gate: 1, cloister: 3 },
  lighting: {
    background: 0x070b0a,
    fog: [0.055, 0.08, 0.078],
    hemiSky: 0xe2e6dc,
    hemiGround: 0x3d3a30,
    hemiIntensity: 0.5,
    key: 0xfff1dc,
    lift: [0.007, 0.011, 0.014],
  },
  glow: { warm: [1.0, 0.55, 0.2], jade: [0.22, 0.75, 0.58] },
};

